/**
 * Elite style presets — neutral color schemes + FX + defaults
 */

export interface EliteNeutrals {
  bg: string;
  bgAlt: string;
  text: string;
  textLight: string;
  textMuted: string;
  border: string;
  borderLight: string;
}

export interface EliteShape {
  radiusSm: string;
  radiusMd: string;
  radiusLg: string;
  radiusXl: string;
}

export interface ElitePreset {
  name: string;
  description: string;
  neutrals: EliteNeutrals;
  fx: { ambientOpacity: number };
  shape?: EliteShape;
  defaults: { fontStack: string; darkMode: boolean };
}

export const eliteStylePresets: Record<string, ElitePreset> = {
  signature: {
    name: 'Signature (Clean Premium)',
    description: 'Modern, ruhig, universell \u2013 ideal f\u00fcr die meisten Branchen.',
    neutrals: {
      bg: '#faf9f7',
      bgAlt: '#f5f4f2',
      text: '#18181b',
      textLight: '#52525b',
      textMuted: '#8a847f',
      border: '#e7e5e4',
      borderLight: '#f5f5f4',
    },
    fx: { ambientOpacity: 0.85 },
    defaults: { fontStack: 'system', darkMode: false },
  },

  swiss: {
    name: 'Swiss Minimal',
    description: 'Typografisch streng, viel Weissraum, klare Hierarchie.',
    neutrals: {
      bg: '#ffffff',
      bgAlt: '#f7f7f8',
      text: '#0a0a0a',
      textLight: '#3f3f46',
      textMuted: '#838390',
      border: '#e4e4e7',
      borderLight: '#f4f4f5',
    },
    fx: { ambientOpacity: 0.55 },
    defaults: { fontStack: 'system', darkMode: false },
  },

  luxe: {
    name: 'Luxury Noir',
    description: 'Dunkel, hochwertig, editorial \u2013 perfekt f\u00fcr Premium/Luxus.',
    neutrals: {
      bg: '#0b0b0f',
      bgAlt: '#111118',
      text: '#f4f4f5',
      textLight: '#d4d4d8',
      textMuted: '#a1a1aa',
      border: '#27272a',
      borderLight: '#1f1f23',
    },
    fx: { ambientOpacity: 0.9 },
    defaults: { fontStack: 'serif', darkMode: true },
  },

  glass: {
    name: 'Glass Aurora',
    description:
      'Soft gradients + Glassmorphism, wirkt modern und \u201Ehigh-end tech\u201C.',
    neutrals: {
      bg: '#0b1220',
      bgAlt: '#0f172a',
      text: '#f8fafc',
      textLight: '#cbd5e1',
      textMuted: '#94a3b8',
      border: '#1f2a44',
      borderLight: '#172033',
    },
    fx: { ambientOpacity: 0.95 },
    defaults: { fontStack: 'system', darkMode: true },
  },

  editorial: {
    name: 'Editorial Serif',
    description: 'Serif-Headline, feine Kontraste \u2013 f\u00fcr Beratung, Kanzlei, Gastro.',
    neutrals: {
      bg: '#fbfbfa',
      bgAlt: '#f2f2ef',
      text: '#111827',
      textLight: '#374151',
      textMuted: '#7a828e',
      border: '#e5e7eb',
      borderLight: '#f3f4f6',
    },
    fx: { ambientOpacity: 0.7 },
    defaults: { fontStack: 'serif', darkMode: false },
  },

  commerce: {
    name: 'Commerce Blue (Direct & Sharp)',
    description:
      'Conversion-orientiert, klare Kontraste und sch\u00e4rfere Radien \u2013 ideal f\u00fcr E\u2011Commerce/Landingpages.',
    neutrals: {
      bg: '#f8f8f8',
      bgAlt: '#ffffff',
      text: '#232323',
      textLight: '#676767',
      textMuted: '#7a828e',
      border: '#dadada',
      borderLight: '#eeeeee',
    },
    fx: { ambientOpacity: 0.55 },
    shape: {
      radiusSm: '2px',
      radiusMd: '6px',
      radiusLg: '10px',
      radiusXl: '14px',
    },
    defaults: { fontStack: 'system', darkMode: false },
  },

  gasserwerk: {
    name: 'CLEAR-4 (Gasserwerk)',
    description:
      'Ruhige Premium-Neutrals, sauberer 4px-Grid Look \u2013 ideal f\u00fcr Beratung, Treuhand, Services.',
    neutrals: {
      bg: '#f5f5f4',
      bgAlt: '#ececea',
      text: '#1f2937',
      textLight: '#4b5563',
      textMuted: '#757d89',
      border: '#e0e0de',
      borderLight: 'rgba(0, 0, 0, 0.06)',
    },
    fx: { ambientOpacity: 0.7 },
    shape: {
      radiusSm: '4px',
      radiusMd: '8px',
      radiusLg: '12px',
      radiusXl: '20px',
    },
    defaults: { fontStack: 'instrument', darkMode: false },
  },
};
